import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useFilterActions } from "../../../ducks/filter/hooks/action";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import { formatCurrency } from "@/utils/formatToMoney";

export function ModalFilters() {
  const { handleModal } = useDialogModal();
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();

  const FormSchema = z.object({
    nsuNumber: z.string().optional(),
    paymentValue: z.string().optional(),
    user: z.string().optional(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      nsuNumber: filterState?.filters?.advancedFilter?.nsuNumber || "",
      paymentValue: filterState?.filters?.advancedFilter?.paymentValue || "",
      user: filterState?.filters?.advancedFilter?.user || "",
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    updateFilter({
      key: "advancedFilter",
      value: {
        ...filterState?.filters?.advancedFilter,
        ...data,
      },
    });
    handleModal({ isOpen: false });
  }

  function handleClearAdvancedFilters() {
    form.reset({
      nsuNumber: "",
      paymentValue: "",
      user: "",
    });

    const { nsuNumber, paymentValue, user, ...requiredFilters } =
      filterState?.filters?.advancedFilter || {};

    updateFilter({
      key: "advancedFilter",
      value: requiredFilters,
    });

    handleModal({ isOpen: false });
  }

  return (
    <div className="grid gap-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* NSU de Autorização */}
            <FormField
              control={form.control}
              name="nsuNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    NSU de Autorização
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite o NSU de autorização"
                      hasError={!!form.formState.errors.nsuNumber}
                    />
                  </FormControl>
                  <FormMessage className="text-red-500 text-xs mt-1" />
                </FormItem>
              )}
            />

            {/* Usuário */}
            <FormField
              control={form.control}
              name="user"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Usuário
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite o nome do usuário"
                      hasError={!!form.formState.errors.user}
                    />
                  </FormControl>
                  <FormMessage className="text-red-500 text-xs mt-1" />
                </FormItem>
              )}
            />

            {/* Valor do Pagamento */}
            <FormField
              control={form.control}
              name="paymentValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Valor do Pagamento
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="R$ 0,00"
                      onChange={(e) =>
                        field.onChange(formatCurrency(e.target.value))
                      }
                      hasError={!!form.formState.errors.paymentValue}
                    />
                  </FormControl>
                  <FormMessage className="text-red-500 text-xs mt-1" />
                </FormItem>
              )}
            />

            {/* Botões */}
            <div className="w-full flex gap-4 justify-end pt-4">
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={handleClearAdvancedFilters}
              >
                Limpar Filtros Avançados
              </Button>
              <Button
                size="sm"
                variant="ghost_none"
                type="button"
                onClick={() => handleModal({ isOpen: false })}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="default" size="sm">
                Aplicar Filtros Avançados
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}